import type {
  ApiErrorCode,
  AuditPage,
  Device,
  DeviceStatus,
  DryRunResult,
  FlowFilters,
  FlowList,
  Me,
  MetricsRange,
  MonthToDateCost,
  Network,
  NetworkCreate,
  NetworkList,
  NetworkMatrix,
  NetworkPatch,
  PolicyDiff,
  PolicyDraft,
  PolicyHistoryEntry,
  PolicyRule,
  PolicyStatus,
  PolicyVersion,
  Relay,
  RouteWarning,
  SiteHost,
  SiteRegistration,
  ThroughputResult,
  TopTalkers,
} from './types';

/** Every non-2xx from vpn-api (CONTRACT 3.2), plus client-side network failures. */
export class ApiError extends Error {
  constructor(
    readonly statusCode: number,
    readonly code: ApiErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiClientOptions {
  baseUrl: string;
  /** Returns the current access token, or null when signed out / expired. */
  getToken: () => string | null | Promise<string | null>;
  /** Called on any 401 so the app can drop the session; the error is still thrown. */
  onUnauthorized?: () => void;
  fetchImpl?: typeof fetch;
}

/** A decoded 2xx: the status, the JSON body (undefined for 204) and the response headers (ETag). */
export interface ApiResponse {
  status: number;
  data: unknown;
  headers: Headers;
}

export interface RequestOptions {
  /** Sent as JSON with `Content-Type: application/json`. */
  body?: unknown;
  /** Extra request headers, e.g. `If-Match`. */
  headers?: Record<string, string>;
}

/** The draft and the ETag to send back as `If-Match` on the next save or publish. */
export interface DraftResult {
  draft: PolicyDraft;
  etag: string;
}

/** A relay response plus whether the server only accepted the work (202). */
export interface RelayResult {
  relay: Relay;
  accepted: boolean;
}

// Used only when the body is not the documented JSON shape (proxy error page, etc.), so the
// UI can still branch on `code`.
function codeForStatus(status: number): ApiErrorCode {
  switch (status) {
    case 401: return 'unauthorized';
    case 403: return 'forbidden';
    case 404: return 'not_found';
    case 400: return 'validation_failed';
    case 409: return 'conflict';
    case 412: return 'precondition_failed';
    case 428: return 'precondition_required';
    case 502: return 'upstream_error';
    default: return 'internal_error';
  }
}

async function decodeError(res: Response): Promise<ApiError> {
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    body = undefined;
  }
  if (body && typeof body === 'object') {
    const b = body as Record<string, unknown>;
    const code = typeof b.code === 'string' ? (b.code as ApiErrorCode) : codeForStatus(res.status);
    const message = typeof b.message === 'string' ? b.message : res.statusText || `HTTP ${res.status}`;
    return new ApiError(typeof b.statusCode === 'number' ? b.statusCode : res.status, code, message);
  }
  return new ApiError(res.status, codeForStatus(res.status), res.statusText || `HTTP ${res.status}`);
}

/** A downloaded file: the body and the name the server suggested. */
export interface Download {
  blob: Blob;
  filename: string;
}

/** `?device=…&port=…` for GET /flows and /flows/export; undefined filters are left out. */
export function flowQuery(f: FlowFilters = {}): string {
  const q = new URLSearchParams();
  if (f.device) q.set('device', f.device);
  if (f.port !== undefined) q.set('port', String(f.port));
  if (f.rule) q.set('rule', f.rule);
  if (f.result) q.set('result', f.result);
  if (f.limit !== undefined) q.set('limit', String(f.limit));
  const qs = q.toString();
  return qs ? `?${qs}` : '';
}

/** The `filename` of a Content-Disposition header (RFC 6266, `filename*` preferred); null if none. */
export function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const star = /filename\*\s*=\s*(?:[\w-]+)?'[^']*'([^;]+)/i.exec(header);
  if (star) {
    try {
      return decodeURIComponent(star[1]!.trim().replace(/^"|"$/g, ''));
    } catch {
      // Malformed percent-encoding: fall through to the plain parameter.
    }
  }
  const plain = /filename\s*=\s*(?:"((?:[^"\\]|\\.)*)"|([^;\s]+))/i.exec(header);
  if (!plain) return null;
  return plain[1] !== undefined ? plain[1].replace(/\\(.)/g, '$1') : plain[2]!;
}

/** Longer than a vpn-api cold start, short enough that a dead upstream shows an error. */
export const REQUEST_TIMEOUT_MS = 30_000;

export function createApiClient(opts: ApiClientOptions) {
  const doFetch = opts.fetchImpl ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
  const base = opts.baseUrl.replace(/\/+$/, '');

  /** The raw 2xx response; any other status is thrown as an ApiError. */
  async function send(method: string, path: string, opt: RequestOptions = {}): Promise<Response> {
    const token = await opts.getToken();
    const headers: Record<string, string> = { Accept: 'application/json', ...opt.headers };
    if (token) headers.Authorization = `Bearer ${token}`;
    let body: string | undefined;
    if (opt.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(opt.body);
    }
    // vpn-api scales to zero, so the first call may wait for a cold start (~10-20 s) - but a
    // request must never hang forever: an unresponsive upstream left the whole UI stuck on
    // "Loading your permissions". Abort after REQUEST_TIMEOUT_MS and surface it as an error.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    let res: Response;
    try {
      res = await doFetch(`${base}${path}`, { method, headers, body, signal: controller.signal });
    } catch (e) {
      if (controller.signal.aborted) {
        throw new ApiError(0, 'network_error', `vpn-api did not answer within ${REQUEST_TIMEOUT_MS / 1000} s.`);
      }
      throw new ApiError(0, 'network_error', e instanceof Error ? e.message : 'Network request failed');
    } finally {
      clearTimeout(timer);
    }
    if (!res.ok) {
      const err = await decodeError(res);
      if (res.status === 401) opts.onUnauthorized?.();
      throw err;
    }
    return res;
  }

  async function request(method: string, path: string, opt: RequestOptions = {}): Promise<ApiResponse> {
    const res = await send(method, path, opt);
    // 204 (revoke) has no body.
    if (res.status === 204) return { status: 204, data: undefined, headers: res.headers };
    return { status: res.status, data: await res.json(), headers: res.headers };
  }

  async function relayCall(method: string, path: string): Promise<RelayResult> {
    const { status, data } = await request(method, path);
    return { relay: data as Relay, accepted: status === 202 };
  }

  /** The device edits all answer `200 {device}`. */
  async function deviceCall(method: string, id: string, sub: string, body?: unknown): Promise<Device> {
    const { data } = await request(method, `/devices/${encodeURIComponent(id)}${sub}`, { body });
    return (data as { device: Device }).device;
  }

  /** GET/PUT /policy/draft: the ETag header, or the revision quoted when a proxy strips it. */
  async function draftCall(method: 'GET' | 'PUT', opt?: RequestOptions): Promise<DraftResult> {
    const { data, headers } = await request(method, '/policy/draft', opt);
    const draft = data as PolicyDraft;
    return { draft, etag: headers.get('ETag') ?? `"${draft.revision}"` };
  }

  return {
    me: async () => (await request('GET', '/me')).data as Me,
    getRelay: async () => (await request('GET', '/relay')).data as Relay,
    // 202 = work begun (or already in progress), 200 = already in the target state.
    startRelay: () => relayCall('POST', '/relay/start'),
    stopRelay: () => relayCall('POST', '/relay/stop'),
    listDevices: async () => ((await request('GET', '/devices')).data as { devices: Device[] }).devices,
    revokeDevice: async (id: string) => {
      await request('DELETE', `/devices/${encodeURIComponent(id)}`);
    },
    // Owner or admin; 409 name_taken.
    renameDevice: (id: string, name: string) => deviceCall('PUT', id, '/name', { name }),
    // Admin only: pin to `ip` (400 outside the device's pool, 409 address_in_use) or unpin.
    setDeviceAddress: (id: string, ip: string) => deviceCall('PUT', id, '/address', { ip }),
    releaseDeviceAddress: (id: string) => deviceCall('DELETE', id, '/address'),
    // Admin only; the server stores them sorted and de-duplicated.
    setDeviceTags: (id: string, tags: string[]) => deviceCall('PUT', id, '/tags', { tags }),
    // Sites, admin only. Replaces the approved routes ([] clears them); 400 for a route the
    // rules refuse or a device that is not a site, 409 route_overlap.
    setDeviceRoutes: (id: string, routes: string[]) => deviceCall('PUT', id, '/routes', { routes }),
    getSiteHosts: async (id: string) =>
      ((await request('GET', `/devices/${encodeURIComponent(id)}/hosts`)).data as { hosts: SiteHost[] }).hosts,
    // The code is forgiving (case, spaces, dashes, the VIP prefix); 404 unknown or expired.
    getClaim: async (code: string) =>
      (await request('GET', `/sites/claims/${encodeURIComponent(code)}`)).data as {
        registration: SiteRegistration;
        warnings: RouteWarning[];
      },
    claimSite: async (body: { claimCode: string; routes: string[]; name?: string; network?: string }) =>
      ((await request('POST', '/sites/claim', { body })).data as { device: Device }).device,
    // Admin only (403 otherwise).
    getDiff: async () => (await request('GET', '/policy/diff')).data as PolicyDiff,
    // Policy: all admin only (CONTRACT 3.4). The draft is optimistic-concurrency: a save or
    // publish carries the ETag it read, 412 precondition_failed = someone saved in between.
    getPolicy: async () => (await request('GET', '/policy')).data as PolicyVersion,
    getDraft: () => draftCall('GET'),
    putDraft: (rules: PolicyRule[], etag: string) => draftCall('PUT', { body: { rules }, headers: { 'If-Match': etag } }),
    // 409 conflict when the draft has no changes.
    publish: async (etag: string) =>
      (await request('POST', '/policy/publish', { headers: { 'If-Match': etag } })).data as PolicyVersion,
    dryRun: async () => (await request('POST', '/policy/dry-run')).data as DryRunResult,
    getHistory: async (limit?: number) =>
      ((await request('GET', `/policy/history${limit !== undefined ? `?limit=${limit}` : ''}`)).data as {
        versions: PolicyHistoryEntry[];
      }).versions,
    // Admin only. Newest first; pass the previous page's `nextBefore` to read further back.
    listAudit: async ({ limit, before }: { limit?: number; before?: string | null } = {}) => {
      const q = new URLSearchParams();
      if (limit !== undefined) q.set('limit', String(limit));
      if (before) q.set('before', before);
      const qs = q.toString();
      return (await request('GET', `/audit${qs ? `?${qs}` : ''}`)).data as AuditPage;
    },
    // Admin only: what the relay last reported (applied version, per-rule hits over 24 h).
    policyStatus: async () => (await request('GET', '/policy/status')).data as PolicyStatus,
    // Connect: one per device of GET /devices, same order.
    deviceStatuses: async () =>
      ((await request('GET', '/devices/status')).data as { statuses: DeviceStatus[] }).statuses,
    // Admin only. Device-view bytes over the range's read window (CONTRACT 4).
    throughput: async (range: MetricsRange = '1h', by: 'total' | 'device' = 'total') =>
      (await request('GET', `/metrics/throughput?range=${range}&by=${by}`)).data as ThroughputResult,
    topTalkers: async (range: MetricsRange = '1h', limit = 5) =>
      (await request('GET', `/metrics/top-talkers?range=${range}&limit=${limit}`)).data as TopTalkers,
    // Admin only; 502 upstream_error when Azure fails and nothing is cached for this month.
    costMonthToDate: async () => (await request('GET', '/cost/month-to-date')).data as MonthToDateCost,
    // Admin only. The last 24 h, newest first.
    listFlows: async (filters?: FlowFilters) => (await request('GET', `/flows${flowQuery(filters)}`)).data as FlowList,
    // The CSV needs the bearer token, so it is fetched here and handed to the page as a blob.
    exportFlows: async (filters?: FlowFilters): Promise<Download> => {
      const res = await send('GET', `/flows/export${flowQuery(filters)}`, { headers: { Accept: 'text/csv' } });
      const filename =
        filenameFromDisposition(res.headers.get('Content-Disposition')) ??
        `flows-${new Date().toISOString().slice(0, 10)}.csv`;
      return { blob: await res.blob(), filename };
    },
    // Networks: all admin only. 409 network_overlap / network_exists / network_in_use.
    listNetworks: async () => (await request('GET', '/networks')).data as NetworkList,
    networkMatrix: async () => (await request('GET', '/networks/matrix')).data as NetworkMatrix,
    createNetwork: async (body: NetworkCreate) =>
      ((await request('POST', '/networks', { body })).data as { network: Network }).network,
    updateNetwork: async (id: string, patch: NetworkPatch) =>
      ((await request('PATCH', `/networks/${encodeURIComponent(id)}`, { body: patch })).data as { network: Network })
        .network,
    deleteNetwork: async (id: string) => {
      await request('DELETE', `/networks/${encodeURIComponent(id)}`);
    },
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
